import { useEffect, useState, useContext, useCallback } from 'react';
import styled, { css } from 'styled-components';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import UserContext from '../hooks/UserContext';
import Api from '../utils/Api';
import Loading from '../components/Loading';

const styles = css`
  padding-top: 20px;
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  gap: 20px;
  & .welcome {
    margin: 0 auto;
    font-size: 2em;
  }
  & .greeting {
    margin: 0 auto;
  }
  & .form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
    .row {
      display: flex;
      flex-wrap: wrap;
      width: 60%;
      gap:20px;
      & .col {
        min-width: 10em;
        display: flex;
        flex-direction: column;
        gap: 20px;
        flex: 1;
      }
      & .label {
        padding-bottom: 20px;
      }
      &.buttons {
        flex-direction: row-reverse;
        align-items: end;
      }
    }
  }

`
const Invitations = ({ className }) => {
  const { invitation } = useParams();
  const navigate = useNavigate();

  const [invitationData, setInvitationData] = useState(null);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);

  const fetchInvitationData = useCallback(async (invitation) => {
    setIsLoading(true);
    const [error, invitationData] = await Api.getShareInvitation(invitation);
    setIsLoading(false);
    if (error) {
      console.error(error);
      return toast.error(Api.error(error));
    }
    if (!invitationData) {
      console.error('No invitation found');
      return toast.error('No invitation found');
    }
    if (invitationData.user) {
      // the invited user exists already
      // If not logged in, go to login and come back here
      if (!user) return navigate('/login', { state: { continueTo: `/share/invitations/${invitation}` } });
      else if (user.email === invitationData.user.email) {
        // current user is the invited user.  Redeem invitation and continue to dashboard
        setIsLoading(true);
        const [error,] = await Api.redeemSharedInvitation(invitation, user);
        setIsLoading(false);
        if (error) {
          console.error(error);
          return toast.error(Api.error(error));
        }
        navigate('/');
      } else {
        // current user is not the invited user, force login again
        toast.error('Your invitation does not match your email address')
        return navigate('/login', { state: { continueTo: `/share/invitations/${invitation}` } });
      }
    }
    else {
      // The invited user does NOT exist
      // If someone is logged in, force logout and come back
      if (user) return navigate('/login', { state: { continueTo: `/share/invitations/${invitation}` } });
      else setInvitationData(invitationData.invitation);
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchInvitationData(invitation);
  }, [invitation, fetchInvitationData]);

  useEffect(() => {
    const { firstname = '', lastname = '', email = '', address = '', phone = '' } = invitationData ?? {};
    setFirstname(firstname);
    setLastname(lastname);
    setEmail(email);
    setPhone(phone);
    setAddress(address);

  }, [invitationData]);

  useEffect(() => {
    let isDisabled = (!(firstname && lastname && email && password && verifyPassword));
    isDisabled = (isDisabled || (password !== verifyPassword));
    setIsDisabled(isDisabled);
  }, [firstname, lastname, email, password, verifyPassword])

  const submit = async () => {
    setIsLoading(true);
    const [err,] = await Api.redeemSharedInvitation(invitation, {
      firstname,
      lastname,
      email,
      phone,
      address,
      password
    });
    setIsLoading(false);
    if (err) {
      toast.error(`Unable to complete your registration: ${Api.error(err)}`);
      console.error(err);
    } else {
      navigate('/login', { state: { message: 'Registration successful, please login' } });
    }
  }

  return (
    <Loading isLoading={isLoading} >
      <div className={className}>
        {
          invitationData &&
          <>
            <div className='welcome'>
              {`Welcome ${invitationData.firstname}!`}
            </div>
            <div className='greeting'>
              Please complete your account setup
            </div>
            <div className='form'>
              <div className='row'>
                <div className='col'>
                  <div className='field'>
                    <div className='label'>
                      Email
                    </div>
                    <input placeholder='Required' disabled value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                  <div className='field'>
                    <div className='label'>
                      First Name
                    </div>
                    <input placeholder='Required' value={firstname} onChange={e => setFirstname(e.target.value)} />
                  </div>
                  <div className='field'>
                    <div className='label'>
                      Last Name
                    </div>
                    <input placeholder='Required' value={lastname} onChange={e => setLastname(e.target.value)} />
                  </div>
                </div>
                <div className='col'>
                  <div className='field'>
                    <div className='label'>
                      Phone Number
                    </div>
                    <input placeholder='Optional' value={phone} onChange={e => setPhone(e.target.value)} />
                  </div>
                  <div className='field'>
                    <div className='label'>
                      Address
                    </div>
                    <input placeholder='Optional' value={address} onChange={e => setAddress(e.target.value)} />
                  </div>
                </div>
                <div className='col'>
                  <div className='field'>
                    <div className='label'>
                      Password
                    </div>
                    <input placeholder='Required' type='password' value={password} onChange={e => setPassword(e.target.value)} />
                  </div>
                  <div className='field'>
                    <div className='label'>
                      Verify Password
                    </div>
                    <input placeholder='Required' type='password' value={verifyPassword} onChange={e => setVerifyPassword(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className='row buttons'>
                <button disabled={isDisabled} className='primary' onClick={submit}>Register</button>
              </div>
            </div>
          </>
        }
      </div >
    </Loading>
  )
}

export default styled(Invitations)`${styles}`;